// Griglia meteo oraria (Open-Meteo) per la sezione Meteo: temperature, nuvole e precipitazioni
// interpolate sull'area visibile e animate nel tempo. Una sola richiesta per area, in cache.
export type MeteoLayer = "radar" | "clouds" | "temperature" | "rain";
export type GridBounds = { west: number; south: number; east: number; north: number };
export type MeteoGrid = GridBounds & { cols: number; rows: number; times: number[]; nowIndex: number; temperature: Float32Array; clouds: Float32Array; rain: Float32Array };
export type GridVariable = "temperature" | "clouds" | "rain";

const COLS = 14, ROWS = 10;
const MAX_AGE = 60 * 60_000;
const pending = new Map<string, Promise<MeteoGrid>>();
const resolved: Array<{ grid: MeteoGrid; at: number }> = [];
let lastCall = Promise.resolve(), lastAt = 0;

// Vista larga: blocchi da 4°; vista ravvicinata: blocchi da 1° con margine ampio. Spostarsi poco non cambia mai la richiesta.
export const snapBounds = (b: GridBounds): GridBounds => {
  const wide = b.east - b.west > 6, step = wide ? 4 : 1, pad = wide ? .25 : .6;
  const padX = (b.east - b.west) * pad, padY = (b.north - b.south) * pad;
  return {
    west: Math.max(-180, Math.floor((b.west - padX) / step) * step), east: Math.min(180, Math.ceil((b.east + padX) / step) * step),
    south: Math.max(-80, Math.floor((b.south - padY) / step) * step), north: Math.min(80, Math.ceil((b.north + padY) / step) * step)
  };
};

export const covers = (grid: GridBounds, view: GridBounds): boolean =>
  view.west >= grid.west && view.east <= grid.east && view.south >= grid.south && view.north <= grid.north
  && grid.east - grid.west <= (view.east - view.west) * 4 + 2;

// Griglia già scaricata (e ancora fresca) che copre la vista: la più fitta disponibile.
export function findGrid(view: GridBounds): MeteoGrid | undefined {
  const now = Date.now();
  return resolved.filter(item => now - item.at < MAX_AGE && covers(item.grid, view)).sort((x, y) => (x.grid.east - x.grid.west) - (y.grid.east - y.grid.west))[0]?.grid;
}

type OpenMeteoPoint = { hourly: { time: string[]; temperature_2m: Array<number | null>; cloud_cover: Array<number | null>; precipitation: Array<number | null> } };

export function fetchGrid(bounds: GridBounds, signal?: AbortSignal): Promise<MeteoGrid> {
  const key = `${bounds.west},${bounds.south},${bounds.east},${bounds.north}`;
  const hit = pending.get(key);
  if (hit) return hit;
  const lats: number[] = [], lons: number[] = [];
  for (let r = 0; r < ROWS; r++) for (let c = 0; c < COLS; c++) {
    lats.push(+(bounds.south + (bounds.north - bounds.south) * r / (ROWS - 1)).toFixed(3));
    lons.push(+(bounds.west + (bounds.east - bounds.west) * c / (COLS - 1)).toFixed(3));
  }
  const url = `https://api.open-meteo.com/v1/forecast?latitude=${lats.join(",")}&longitude=${lons.join(",")}&hourly=temperature_2m,cloud_cover,precipitation&past_days=1&forecast_days=3&timezone=UTC`;
  // Al massimo una richiesta ogni 2 secondi verso Open-Meteo, qualunque cosa faccia l'utente.
  const turn = lastCall.then(() => new Promise<void>(resolve => setTimeout(() => { lastAt = Date.now(); resolve(); }, Math.max(0, lastAt + 2000 - Date.now()))));
  lastCall = turn;
  const promise = turn.then(() => { if (signal?.aborted) throw new DOMException("Annullata", "AbortError"); return fetch(url, { signal }); }).then(async response => {
    if (!response.ok) throw new Error(response.status === 429 ? "Open-Meteo limita le richieste: riprovo tra poco" : `Dati meteo non disponibili (${response.status})`);
    const data = await response.json() as OpenMeteoPoint[];
    const first = data[0]?.hourly.time ?? [];
    const length = first.length, size = COLS * ROWS;
    const temperature = new Float32Array(size * length), clouds = new Float32Array(size * length), rain = new Float32Array(size * length);
    data.forEach((point, p) => {
      for (let t = 0; t < length; t++) {
        const i = t * size + p;
        temperature[i] = point.hourly.temperature_2m[t] ?? 0; clouds[i] = point.hourly.cloud_cover[t] ?? 0; rain[i] = point.hourly.precipitation[t] ?? 0;
      }
    });
    const times = first.map(value => Date.parse(`${value}Z`));
    const now = Date.now();
    const nowIndex = Math.max(0, times.findIndex((time, i) => time <= now && (times[i + 1] ?? Infinity) > now));
    const grid: MeteoGrid = { ...bounds, cols: COLS, rows: ROWS, times, nowIndex, temperature, clouds, rain };
    resolved.push({ grid, at: Date.now() });
    if (resolved.length > 6) resolved.shift();
    return grid;
  });
  promise.then(() => pending.delete(key), () => pending.delete(key));
  pending.set(key, promise);
  return promise;
}

// Valore alla posizione di griglia (fx, fy in celle) e all'ora frazionaria t, interpolato in spazio e tempo.
export function sampleGrid(grid: MeteoGrid, variable: GridVariable, fx: number, fy: number, t: number): number {
  const data = grid[variable], size = grid.cols * grid.rows, last = grid.times.length - 1;
  const x = Math.min(grid.cols - 1.001, Math.max(0, fx)), y = Math.min(grid.rows - 1.001, Math.max(0, fy));
  const tt = Math.min(last - .001, Math.max(0, t));
  const x0 = Math.floor(x), y0 = Math.floor(y), t0 = Math.floor(tt), fx0 = x - x0, fy0 = y - y0, at = tt - t0;
  // Raccordo morbido tra i nodi: con celle larghe l'interpolazione lineare lascia strisce visibili.
  const ax = fx0 * fx0 * (3 - 2 * fx0), ay = fy0 * fy0 * (3 - 2 * fy0);
  const plane = (ti: number) => {
    const o = ti * size + y0 * grid.cols + x0;
    const top = data[o]! * (1 - ax) + data[o + 1]! * ax, bottom = data[o + grid.cols]! * (1 - ax) + data[o + grid.cols + 1]! * ax;
    return top * (1 - ay) + bottom * ay;
  };
  return plane(t0) * (1 - at) + plane(Math.min(last, t0 + 1)) * at;
}

type Stop = [number, [number, number, number]];
const TEMPERATURE: Stop[] = [[-20, [106, 61, 154]], [-10, [59, 76, 192]], [0, [60, 157, 216]], [6, [60, 197, 197]], [11, [76, 191, 106]], [16, [166, 217, 74]], [21, [245, 224, 74]], [26, [245, 165, 58]], [31, [232, 85, 58]], [38, [160, 29, 58]]];
const RAIN: Stop[] = [[.1, [120, 190, 255]], [.5, [50, 140, 235]], [1.5, [40, 190, 110]], [3, [235, 220, 60]], [6, [240, 140, 40]], [12, [225, 50, 50]], [25, [160, 40, 170]]];

function ramp(stops: Stop[], value: number): [number, number, number] {
  if (value <= stops[0]![0]) return stops[0]![1];
  for (let i = 1; i < stops.length; i++) {
    const [v1, c1] = stops[i]!, [v0, c0] = stops[i - 1]!;
    if (value <= v1) { const f = (value - v0) / (v1 - v0); return [c0[0] + (c1[0] - c0[0]) * f, c0[1] + (c1[1] - c0[1]) * f, c0[2] + (c1[2] - c0[2]) * f]; }
  }
  return stops[stops.length - 1]![1];
}

// Colore RGBA di una cella. Le nuvole hanno una leggera texture fissa sul territorio per sembrare masse reali.
export function cellColor(layer: GridVariable, value: number, lat: number, lon: number, out: Uint8ClampedArray, offset: number): void {
  let r = 0, g = 0, b = 0, a = 0;
  if (layer === "temperature") { [r, g, b] = ramp(TEMPERATURE, value); a = 165; }
  else if (layer === "rain") {
    if (value >= .08) { [r, g, b] = ramp(RAIN, value); a = Math.min(230, 70 + Math.min(1, value / 1.5) * 160); }
  } else {
    const noise = .88 + .12 * Math.sin(lon * 5.1 + Math.sin(lat * 3.7) * 2) * Math.cos(lat * 6.3 - lon * 1.9);
    const density = Math.max(0, Math.min(1, ((value - 8) / 72) * noise));
    const shade = 255 - density * 55;
    r = shade; g = shade + 3; b = shade + 10; a = Math.pow(density, .9) * 235;
  }
  out[offset] = r; out[offset + 1] = g; out[offset + 2] = b; out[offset + 3] = a;
}

const css = (stops: Stop[]) => stops.map(([, c]) => `rgb(${c[0]},${c[1]},${c[2]})`).join(", ");
export const LEGENDS: Record<MeteoLayer, { title: string; gradient: string; from: string; to: string; note: string }> = {
  radar: { title: "Radar pioggia", gradient: `linear-gradient(90deg, ${css(RAIN)})`, from: "debole", to: "intensa", note: "Radar reale delle ultime due ore (RainViewer): la pioggia che sta cadendo adesso e come si sposta." },
  rain: { title: "Piovosità prevista", gradient: `linear-gradient(90deg, ${css(RAIN)})`, from: "0,1 mm/h", to: "25 mm/h", note: "Precipitazioni orarie previste, da ieri a dopodomani." },
  temperature: { title: "Temperature", gradient: `linear-gradient(90deg, ${css(TEMPERATURE)})`, from: "−20 °C", to: "38 °C", note: "Temperatura a 2 metri in falsi colori, ora per ora." },
  clouds: { title: "Nuvole", gradient: "linear-gradient(90deg, #ffffff00, #ffffffb0, #c8cfdc)", from: "sereno", to: "coperto", note: "Copertura nuvolosa prevista: le masse si muovono ora dopo ora." }
};
